<script>
import { useVuelidate } from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';
import InboxDropdownItem from 'dashboard/components/widgets/InboxDropdownItem.vue';
import { useAlert } from 'dashboard/composables';
import {
  buildPhoneInputState,
  CONTACT_ENTITY_TYPES,
} from 'dashboard/helper/contactEntity';
import { getInboxSource } from 'dashboard/helper/inbox';
import parsePhoneNumber from 'libphonenumber-js/max';
import {
  normalizePhoneForInput,
  PHONE_VALIDATION_ERRORS,
  validatePhoneForInput,
} from 'shared/helpers/phoneNumberValidation';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import inboxMixin, { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import { mapActions, mapGetters } from 'vuex';

// Aba exibida quando o modal e aberto sem `initialState`: 1 = novo contato.
const DEFAULT_ACTIVE_TAB = 1;

export default {
  name: 'NewConversationModal',
  components: {
    InboxDropdownItem,
  },
  mixins: [inboxMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    initialState: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      activeTab: DEFAULT_ACTIVE_TAB,
      contactName: '',
      contactEmail: '',
      phoneNumber: '',
      activeDialCode: '',
      searchQuery: '',
      isSearching: false,
      selectedContact: null,
      targetInbox: {},
      inProgress: false,
      hasAppliedInitialState: false,
    };
  },
  validations() {
    if (this.activeTab === 1) {
      return {
        contactName: {},
        contactEmail: this.showEmailField
          ? {
              required,
              email,
            }
          : {},
        phoneNumber: this.showPhoneField
          ? {
              required,
            }
          : {},
        targetInbox: {
          required,
          hasInbox: val => val && val.inbox && val.inbox.id,
        },
      };
    }
    return {
      targetInbox: {
        required,
        hasInbox: val => val && val.inbox && val.inbox.id,
      },
    };
  },
  computed: {
    ...mapGetters({
      searchResults: 'conversationSearch/getContactRecords',
      allInboxes: 'inboxes/getInboxes',
      globalConfig: 'globalConfig/get',
      getContact: 'contacts/getContact',
      currentUser: 'getCurrentUser',
    }),
    selectedInbox() {
      return this.targetInbox?.inbox || null;
    },
    isTabLocked() {
      return !!this.initialState?.lockToTab;
    },
    inbox() {
      return this.selectedInbox || {};
    },
    inboxes() {
      if (this.activeTab === 0) {
        const contact = this.getContact(this.selectedContact?.id);
        return (
          contact?.contactable_inboxes?.map(item => ({
            ...item,
            option_id: `${item.inbox.id}-${item.source_id}`,
          })) || []
        );
      }
      if (this.activeTab === 1) {
        return this.allInboxes
          .filter(inbox => this.isSupportedNewContactInbox(inbox))
          .filter(inbox => this.isInboxCompatibleWithCurrentEntity(inbox))
          .map(inbox => ({
            inbox,
            source_id: this.computeSourceIdForInbox(inbox),
            option_id: `new-contact-${inbox.id}`,
          }));
      }
      return [];
    },
    showEmailField() {
      return (
        this.activeTab === 1 &&
        this.selectedInbox?.channel_type === INBOX_TYPES.EMAIL
      );
    },
    showPhoneField() {
      return this.activeTab === 1 && this.isPhoneBasedInbox(this.selectedInbox);
    },
    searchButtonClass() {
      return this.searchQuery !== ''
        ? 'opacity-100 translate-x-0 visible'
        : '-translate-x-px opacity-0 invisible';
    },
    parsePhoneNumber() {
      return parsePhoneNumber(this.phoneNumber);
    },
    isPhoneNumberNotValid() {
      if (!this.phoneNumber) return false;
      return !this.phoneValidation.isValid;
    },
    phoneNumberError() {
      if (
        this.phoneValidation.errorKey ===
        PHONE_VALIDATION_ERRORS.MISSING_DIAL_CODE
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DIAL_CODE_ERROR');
      }
      if (
        this.phoneValidation.errorKey ===
        PHONE_VALIDATION_ERRORS.INVALID_BRAZIL_AREA_CODE
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.BRAZIL_AREA_CODE_ERROR');
      }
      if (
        this.phoneValidation.errorKey ===
        PHONE_VALIDATION_ERRORS.PHONE_TOO_SHORT
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.TOO_SHORT_ERROR');
      }
      if (
        this.phoneValidation.errorKey === PHONE_VALIDATION_ERRORS.PHONE_TOO_LONG
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.TOO_LONG_ERROR');
      }
      if (
        this.phoneValidation.errorKey ===
        PHONE_VALIDATION_ERRORS.PHONE_INVALID_LENGTH
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.INVALID_LENGTH_ERROR');
      }
      if (
        this.phoneValidation.errorKey === PHONE_VALIDATION_ERRORS.INVALID_PHONE
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.ERROR');
      }
      return '';
    },
    normalizedPhoneNumber() {
      return normalizePhoneForInput(this.phoneNumber, this.activeDialCode);
    },
    phoneValidation() {
      return validatePhoneForInput(this.phoneNumber, this.activeDialCode);
    },
    setPhoneNumber() {
      if (this.parsePhoneNumber && this.parsePhoneNumber.countryCallingCode) {
        return this.phoneNumber;
      }
      if (this.phoneNumber === '' && this.activeDialCode !== '') {
        return '';
      }
      return this.normalizedPhoneNumber;
    },
    hasValidEmailEntity() {
      const emailValue = this.contactEmail.trim();
      if (!emailValue) {
        return false;
      }

      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
    },
    hasValidPhoneEntity() {
      return Boolean(this.setPhoneNumber && !this.isPhoneNumberNotValid);
    },
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.applyInitialState();
        return;
      }

      this.hasAppliedInitialState = false;
    },
    inboxes(newInboxes) {
      if (!newInboxes?.length) {
        this.targetInbox = {};
        return;
      }

      if (this.targetInbox?.inbox?.id) {
        const matchingInbox = newInboxes.find(
          item => item.inbox.id === this.targetInbox.inbox.id
        );

        if (matchingInbox) {
          this.targetInbox = matchingInbox;
          return;
        }

        this.targetInbox = {};
      }

      const initialInbox = this.findInitialInbox(newInboxes);
      if (initialInbox) {
        this.targetInbox = initialInbox;
        return;
      }

      const lastSelectedInboxId = localStorage.getItem('lastSelectedInboxId');
      if (lastSelectedInboxId) {
        const lastSelectedInbox = newInboxes.find(
          item => item.inbox.id === Number(lastSelectedInboxId)
        );
        if (lastSelectedInbox) {
          this.targetInbox = lastSelectedInbox;
        }
      }
    },
    targetInbox(newInbox) {
      if (newInbox && newInbox.inbox && newInbox.inbox.id) {
        localStorage.setItem('lastSelectedInboxId', newInbox.inbox.id);
      }
      if (this.activeTab === 1) {
        this.resetNewContactChannelFields(newInbox?.inbox);
      }
    },
    selectedContact(newContact) {
      if (newContact && newContact.id) {
        this.$store.dispatch('contacts/fetchContactableInbox', newContact.id);
      }
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
    if (this.show) {
      this.applyInitialState();
    }
  },
  methods: {
    ...mapActions('conversationSearch', [
      'contactSearch',
      'clearSearchResults',
    ]),
    ...mapActions('contactConversations', ['create']),

    resetForm() {
      this.activeTab = DEFAULT_ACTIVE_TAB;
      this.contactName = '';
      this.contactEmail = '';
      this.phoneNumber = '';
      this.activeDialCode = '';
      this.searchQuery = '';
      this.selectedContact = null;
      this.targetInbox = {};
      this.hasAppliedInitialState = false;
      this.v$.$reset();
    },
    onTabChange(index) {
      if (this.isTabLocked) {
        return;
      }

      this.activeTab = index;
      this.contactName = '';
      this.contactEmail = '';
      this.phoneNumber = '';
      this.activeDialCode = '';
      this.selectedContact = null;
      this.targetInbox = {};
      this.v$.$reset();

      if (index === 1) {
        this.applyInitialEntityPrefill();
      }
    },
    applyInitialState() {
      this.resetForm();

      const {
        activeTab = DEFAULT_ACTIVE_TAB,
        selectedContact = null,
        searchQuery = '',
        contactName = '',
        contactEmail = '',
        prefilledPhone = '',
        autoSearch = false,
      } = this.initialState || {};

      this.activeTab = activeTab;
      this.selectedContact = selectedContact;
      this.searchQuery =
        searchQuery ||
        selectedContact?.name ||
        selectedContact?.phone_number ||
        selectedContact?.email ||
        '';
      this.contactName = contactName;
      this.contactEmail = contactEmail;

      if (prefilledPhone) {
        if (activeTab === 1) {
          const { phoneNumber, activeDialCode } =
            buildPhoneInputState(prefilledPhone);
          this.phoneNumber = phoneNumber;
          this.activeDialCode = activeDialCode;
        } else {
          const { phoneNumber, activeDialCode } =
            buildPhoneInputState(prefilledPhone);
          this.phoneNumber = phoneNumber;
          this.activeDialCode = activeDialCode;
        }
      }

      this.hasAppliedInitialState = true;

      const initialInbox = this.findInitialInbox(this.inboxes);
      if (initialInbox) {
        this.targetInbox = initialInbox;
      }

      if (
        activeTab === 0 &&
        autoSearch &&
        this.searchQuery.length >= 2 &&
        !this.selectedContact
      ) {
        this.performSearch();
      }
    },
    applyInitialEntityPrefill() {
      const { contactEmail = '', prefilledPhone = '' } =
        this.initialState || {};

      this.contactEmail = contactEmail;

      if (prefilledPhone) {
        const { phoneNumber, activeDialCode } =
          buildPhoneInputState(prefilledPhone);
        this.phoneNumber = phoneNumber;
        this.activeDialCode = activeDialCode;
      }

      const initialInbox = this.findInitialInbox(this.inboxes);
      if (initialInbox) {
        this.targetInbox = initialInbox;
      }
    },
    findInitialInbox(inboxOptions) {
      if (!this.hasAppliedInitialState || !inboxOptions?.length) {
        return null;
      }

      const preferredEntityType = this.initialState?.preferredEntityType;
      if (preferredEntityType === CONTACT_ENTITY_TYPES.EMAIL) {
        return (
          inboxOptions.find(
            option => option.inbox.channel_type === INBOX_TYPES.EMAIL
          ) || null
        );
      }

      if (preferredEntityType === CONTACT_ENTITY_TYPES.PHONE) {
        return (
          inboxOptions.find(option => this.isPhoneBasedInbox(option.inbox)) ||
          null
        );
      }

      // Sem entidade preferida, cai na memoria da ultima caixa usada. O
      // fallback precisa morar AQUI, e nao no watcher de `inboxes`:
      // applyInitialState e applyInitialEntityPrefill chamam este metodo
      // direto, sem passar pelo watcher. Com o fallback so la, o modal aberto
      // pelo PopOverSearch abria sem caixa nenhuma — o oposto do pretendido.
      const lastSelectedInboxId = localStorage.getItem('lastSelectedInboxId');
      if (lastSelectedInboxId) {
        return (
          inboxOptions.find(
            option => option.inbox.id === Number(lastSelectedInboxId)
          ) || null
        );
      }

      return null;
    },
    resetNewContactChannelFields(inbox) {
      if (!inbox) {
        return;
      }

      if (inbox.channel_type === INBOX_TYPES.EMAIL) {
        this.phoneNumber = '';
        this.activeDialCode = '';
        this.v$.phoneNumber?.$reset();
        return;
      }

      if (this.isPhoneBasedInbox(inbox)) {
        this.contactEmail = '';
        this.v$.contactEmail?.$reset();
      }
    },
    isPhoneBasedInbox(inbox) {
      if (!inbox) {
        return false;
      }

      if (inbox.channel_type === INBOX_TYPES.WHATSAPP) {
        return true;
      }

      if (inbox.channel_type === INBOX_TYPES.SMS) {
        return true;
      }

      return (
        inbox.channel_type === INBOX_TYPES.TWILIO &&
        ['sms', 'whatsapp'].includes(inbox.medium)
      );
    },
    isSupportedNewContactInbox(inbox) {
      if (!inbox) {
        return false;
      }

      return (
        inbox.channel_type === INBOX_TYPES.EMAIL ||
        this.isPhoneBasedInbox(inbox)
      );
    },
    isInboxCompatibleWithCurrentEntity(inbox) {
      // Este filtro serve ao fluxo de entidade detectada, em que o modal abre
      // ja com o e-mail ou o telefone vindo do `initialState`. Aplicá-lo tambem
      // ao que o usuario digita tira do dropdown justamente a caixa para a qual
      // ele quer trocar, e o `resetNewContactChannelFields` -- que existe para
      // limpar o campo do outro canal nessa troca -- nunca chega a rodar.
      if (!this.initialState?.preferredEntityType) {
        return true;
      }

      const shouldUseOnlyEmailInboxes =
        this.hasValidEmailEntity && !this.hasValidPhoneEntity;
      const shouldUseOnlyPhoneInboxes =
        this.hasValidPhoneEntity && !this.hasValidEmailEntity;

      if (shouldUseOnlyEmailInboxes) {
        return inbox.channel_type === INBOX_TYPES.EMAIL;
      }

      if (shouldUseOnlyPhoneInboxes) {
        return this.isPhoneBasedInbox(inbox);
      }

      return true;
    },
    computeSourceIdForInbox(inbox) {
      if (!inbox) {
        return '';
      }

      if (inbox.channel_type === INBOX_TYPES.EMAIL) {
        return this.contactEmail.trim();
      }

      const phoneNumberWithPlus = this.setPhoneNumber;
      if (!phoneNumberWithPlus || this.isPhoneNumberNotValid) {
        return '';
      }

      switch (inbox.channel_type) {
        case INBOX_TYPES.WHATSAPP:
          return phoneNumberWithPlus.startsWith('+')
            ? phoneNumberWithPlus.substring(1)
            : phoneNumberWithPlus;
        case INBOX_TYPES.SMS:
          return phoneNumberWithPlus;
        case INBOX_TYPES.TWILIO:
          return inbox.medium === 'whatsapp'
            ? `whatsapp:${phoneNumberWithPlus}`
            : phoneNumberWithPlus;
        default:
          return '';
      }
    },
    checkIfWhatsAppChannel(inbox) {
      const { channel_type: channelType, medium = '' } = inbox;
      const isTwilioWhatsApp =
        channelType === 'Channel::TwilioSms' && medium === 'whatsapp';
      return channelType === 'Channel::Whatsapp' || isTwilioWhatsApp;
    },
    onClose() {
      this.clearSearchResults();
      this.resetForm();
      this.$emit('close');
    },
    async onSubmit() {
      this.v$.$touch();
      if (
        this.v$.$invalid ||
        (this.showPhoneField && this.isPhoneNumberNotValid)
      ) {
        return;
      }

      this.inProgress = true;
      try {
        let contactId;
        let sourceId;
        let inbox;

        if (this.activeTab === 1) {
          const { inbox: selectedInbox } = this.targetInbox;
          const generatedSourceId = this.computeSourceIdForInbox(selectedInbox);
          const contactPayload = {
            name: this.contactName.trim(),
            inbox_id: selectedInbox.id,
            source_id: generatedSourceId,
          };

          if (this.showEmailField) {
            contactPayload.email = this.contactEmail.trim();
          }

          if (this.showPhoneField) {
            contactPayload.phone_number = this.setPhoneNumber;
          }

          const newContact = await this.$store.dispatch(
            'contacts/create',
            contactPayload
          );
          contactId = newContact.id;
          sourceId = generatedSourceId;
          inbox = selectedInbox;
        } else {
          contactId = this.selectedContact.id;
          sourceId = this.targetInbox.source_id;
          inbox = this.targetInbox.inbox;
        }

        const isFromWhatsApp = this.checkIfWhatsAppChannel(inbox);
        const payload = {
          inboxId: inbox.id,
          sourceId,
          contactId,
          assigneeId: this.currentUser.id,
        };
        const conversation = await this.create({
          params: payload,
          isFromWhatsApp,
        });

        this.onClose();

        if (conversation) {
          const action = {
            type: 'link',
            to: `/app/accounts/${conversation.account_id}/conversations/${conversation.id}`,
            message: this.$t(
              'SIDEBAR.NEW_CONVERSATION_MODAL.GO_TO_CONVERSATION'
            ),
          };
          useAlert(
            this.$t('SIDEBAR.NEW_CONVERSATION_MODAL.SUCCESS_MESSAGE'),
            action
          );
        }
      } catch (error) {
        console.log(error);
        if (error instanceof DuplicateContactException) {
          if (Array.isArray(error.data) && error.data.includes('email')) {
            useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
          } else {
            useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(this.$t('NEW_CONVERSATION.FORM.ERROR_MESSAGE'));
        }
      } finally {
        this.inProgress = false;
      }
    },
    async handleConversationSubmit(params, isFromWhatsApp) {
      const data = await this.create({
        params,
        isFromWhatsApp,
      });
      return data;
    },
    async performSearch() {
      if (this.searchQuery.length < 2) return;
      this.isSearching = true;
      try {
        await this.contactSearch({ q: this.searchQuery });
      } catch (error) {
        // Handle error silently
      } finally {
        this.isSearching = false;
      }
    },
    selectContact(contact) {
      this.selectedContact = contact;
      this.searchQuery = contact.name;
      this.clearSearchResults();
      this.targetInbox = {};
    },
    computedInboxSource(inbox) {
      if (!inbox.channel_type) return '';
      const classByType = getInboxSource(
        inbox.channel_type,
        inbox.phone_number,
        inbox
      );
      return classByType;
    },
    onPhoneNumberInputChange(value, code) {
      this.activeDialCode = code;
    },
    setPhoneCode(code) {
      if (this.phoneNumber !== '' && this.parsePhoneNumber) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;
        if (dialCode === code) {
          return;
        }
        this.activeDialCode = `+${dialCode}`;
        const newPhoneNumber = this.phoneNumber.replace(
          `+${dialCode}`,
          `${code}`
        );
        this.phoneNumber = newPhoneNumber;
      } else {
        this.activeDialCode = code;
      }
    },
  },
};
</script>

<template>
  <woot-modal :show.sync="show" :on-close="onClose">
    <woot-modal-header
      :header-title="$t('SIDEBAR.NEW_CONVERSATION_MODAL.TITLE')"
      :header-content="$t('SIDEBAR.NEW_CONVERSATION_MODAL.DESCRIPTION')"
    />
    <div>
      <woot-tabs
        v-if="!isTabLocked"
        :index="activeTab"
        class="px-4"
        @change="onTabChange"
      >
        <woot-tabs-item
          :name="$t('SIDEBAR.NEW_CONVERSATION_MODAL.TABS.EXISTING_CONTACT')"
          :show-badge="false"
        />
        <woot-tabs-item
          :name="$t('SIDEBAR.NEW_CONVERSATION_MODAL.TABS.NEW_CONTACT')"
          :show-badge="false"
        />
      </woot-tabs>
      <div v-if="activeTab === 0" class="pt-4 px-8">
        <h2 class="text-lg font-semibold text-body">
          {{
            $t(
              'SIDEBAR.NEW_CONVERSATION_MODAL.FORM.EXISTING_CONTACT.DESCRIPTION'
            )
          }}
        </h2>
        <div
          class="max-w-[400px] min-w-[100px] flex items-center relative mb-4"
        >
          <div class="flex items-center absolute h-full left-2.5">
            <fluent-icon
              icon="search"
              class="h-5 text-sm leading-9 text-muted"
            />
          </div>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="$t('CONTACTS_PAGE.SEARCH_INPUT_PLACEHOLDER')"
            class="!pl-9 !pr-20 !text-sm !w-full !h-[2.375rem] !m-0 border-line-faint"
            @keyup.enter="performSearch"
          />
          <woot-button
            :is-loading="isSearching"
            class="absolute h-8 px-2 py-0 ml-2 transition-transform duration-100 ease-linear clear right-1"
            :class-names="searchButtonClass"
            @click="performSearch"
          >
            {{ $t('CONTACTS_PAGE.SEARCH_BUTTON') }}
          </woot-button>
        </div>

        <!-- Search Results -->
        <div v-if="searchResults.length" class="mt-4 overflow-y-auto max-h-48">
          <ul>
            <li
              v-for="contact in searchResults"
              :key="contact.id"
              class="flex items-center p-2 rounded-md cursor-pointer hover:bg-surface-hover"
              @click="selectContact(contact)"
            >
              <woot-thumbnail
                :src="contact.thumbnail"
                :username="contact.name"
                size="32px"
                class="mr-4"
              />
              <div>
                <p class="font-semibold text-body">
                  {{ contact.name }}
                </p>
                <p class="text-sm text-muted">
                  {{ contact.phone_number || contact.email }}
                </p>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div v-if="activeTab === 1" class="pt-4 px-8">
        <h2 class="text-lg font-semibold text-body">
          {{
            $t('SIDEBAR.NEW_CONVERSATION_MODAL.FORM.NEW_CONTACT.DESCRIPTION')
          }}
        </h2>
        <label class="mt-4 block text-base font-semibold text-body">
          {{ $t('SIDEBAR.NEW_CONVERSATION_MODAL.FORM.INBOX.LABEL') }}
        </label>
        <div
          v-if="show"
          class="mt-2 multiselect-wrap--small"
          :class="{ 'has-multi-select-error': v$.targetInbox.$error }"
        >
          <multiselect
            v-model="targetInbox"
            track-by="option_id"
            label="inbox.name"
            :placeholder="$t('FORMS.MULTISELECT.SELECT')"
            selected-label=""
            select-label=""
            deselect-label=""
            :max-height="160"
            close-on-select
            :options="inboxes"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <InboxDropdownItem
                v-if="option.inbox && option.inbox.name"
                :name="option.inbox.name"
                :inbox-identifier="computedInboxSource(option.inbox)"
                :channel-type="option.inbox.channel_type"
              />
              <span v-else>
                {{ $t('NEW_CONVERSATION.FORM.INBOX.PLACEHOLDER') }}
              </span>
            </template>
            <template slot="option" slot-scope="{ option }">
              <InboxDropdownItem
                :name="option.inbox.name"
                :inbox-identifier="computedInboxSource(option.inbox)"
                :channel-type="option.inbox.channel_type"
              />
            </template>
          </multiselect>
        </div>
        <div v-if="selectedInbox" class="mt-4">
          <label class="text-base font-semibold text-body">
            {{
              $t('SIDEBAR.NEW_CONVERSATION_MODAL.FORM.NEW_CONTACT.NAME_LABEL')
            }}
            <span class="font-normal text-subtle">
              {{
                $t(
                  'SIDEBAR.NEW_CONVERSATION_MODAL.FORM.NEW_CONTACT.OPTIONAL_LABEL'
                )
              }}
            </span>
          </label>
          <input
            v-model="contactName"
            type="text"
            class="w-full px-2 py-1 mt-2 border rounded-md border-line focus:outline-none focus:ring-2 focus:ring-woot-500 !text-sm !h-[2.375rem] placeholder:text-base"
            :placeholder="
              $t(
                'SIDEBAR.NEW_CONVERSATION_MODAL.FORM.NEW_CONTACT.NAME_PLACEHOLDER'
              )
            "
          />
        </div>
        <div v-if="showEmailField" class="mt-4">
          <label
            :class="{ error: v$.contactEmail.$error }"
            class="text-base font-semibold text-body"
          >
            {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.LABEL') }}
          </label>
          <input
            v-model.trim="contactEmail"
            type="email"
            class="w-full px-2 py-1 mt-2 border rounded-md border-line focus:outline-none focus:ring-2 focus:ring-woot-500 !text-sm !h-[2.375rem] placeholder:text-base"
            :placeholder="$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.PLACEHOLDER')"
            @blur="v$.contactEmail.$touch"
          />
          <span
            v-if="v$.contactEmail.$error"
            class="message text-red-600 dark:text-red-400"
          >
            {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.ERROR') }}
          </span>
        </div>
        <div v-if="showPhoneField" class="mt-4">
          <label class="text-base font-semibold text-body">
            {{
              $t('SIDEBAR.NEW_CONVERSATION_MODAL.FORM.NEW_CONTACT.NUMBER_LABEL')
            }}
          </label>
          <label
            :class="{
              error: isPhoneNumberNotValid,
            }"
          >
            <woot-phone-input
              v-model="phoneNumber"
              :value="phoneNumber"
              :dial-code="activeDialCode"
              :error="isPhoneNumberNotValid"
              :placeholder="$t('CONTACT_FORM.FORM.PHONE_NUMBER.PLACEHOLDER')"
              class="!text-sm"
              @input="onPhoneNumberInputChange"
              @blur="v$.phoneNumber.$touch"
              @setCode="setPhoneCode"
            />
            <span v-if="isPhoneNumberNotValid" class="message">
              {{ phoneNumberError }}
            </span>
          </label>
        </div>
      </div>
    </div>

    <div v-if="activeTab === 0 && selectedContact" class="w-full px-8">
      <label class="text-base font-semibold text-body">
        {{ $t('SIDEBAR.NEW_CONVERSATION_MODAL.FORM.INBOX.LABEL') }}
      </label>
      <div
        v-if="show"
        class="multiselect-wrap--small"
        :class="{ 'has-multi-select-error': v$.targetInbox.$error }"
      >
        <multiselect
          v-model="targetInbox"
          track-by="option_id"
          label="inbox.name"
          :placeholder="$t('FORMS.MULTISELECT.SELECT')"
          selected-label=""
          select-label=""
          deselect-label=""
          :max-height="160"
          close-on-select
          :options="inboxes"
        >
          <template slot="singleLabel" slot-scope="{ option }">
            <InboxDropdownItem
              v-if="option.inbox && option.inbox.name"
              :name="option.inbox.name"
              :inbox-identifier="computedInboxSource(option.inbox)"
              :channel-type="option.inbox.channel_type"
            />
            <span v-else>
              {{ $t('NEW_CONVERSATION.FORM.INBOX.PLACEHOLDER') }}
            </span>
          </template>
          <template slot="option" slot-scope="{ option }">
            <InboxDropdownItem
              :name="option.inbox.name"
              :inbox-identifier="computedInboxSource(option.inbox)"
              :channel-type="option.inbox.channel_type"
            />
          </template>
        </multiselect>
      </div>
    </div>

    <div class="flex justify-end w-full p-4">
      <woot-button class="mr-2" variant="clear" @click="onClose">
        {{ $t('SIDEBAR.NEW_CONVERSATION_MODAL.BUTTONS.CANCEL') }}
      </woot-button>
      <woot-button :is-loading="inProgress" @click="onSubmit">
        {{ $t('SIDEBAR.NEW_CONVERSATION_MODAL.BUTTONS.CREATE') }}
      </woot-button>
    </div>
  </woot-modal>
</template>
